import { Card, Select, Spin } from 'antd';
import Meta from 'antd/lib/card/Meta';
import { DefaultOptionType } from 'antd/lib/select';
import debounce from 'lodash/debounce';
import { User, UsersApi } from 'osep-generator-service-client-v1';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useApiConfig } from '../../hooks/useApiConfig';
import { AzureEntraIdUserVariableModel } from '../../iasConfigSchema';
import VariableFormItem from '../variables/VariableFormItem';

type UserOption = User & DefaultOptionType;

const UserSelect: React.FC<{
  variable: AzureEntraIdUserVariableModel;
  disabled: boolean;
}> = ({ variable, disabled }) => {
  const [options, setOptions] = useState<UserOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { getApiConfig, axiosInstance } = useApiConfig();
  const searchRef = useRef(0);

  const handleSearch = useCallback(
    async (searchTerm: string) => {
      searchRef.current += 1;
      const searchId = searchRef.current;

      const apiConfig = await getApiConfig();
      const usersApi = new UsersApi(apiConfig, undefined, axiosInstance);

      setOptions([]);
      setIsLoading(true);

      const users = await usersApi.searchUsers({ searchTerm, limit: 10 });
      const userOptions: UserOption[] = users.data.users.map((user) => ({
        ...user,
        label: user.displayName || user.userPrincipalName || user.mail || '',
        value: variable.id_property === 'id' ? user.id : user.userPrincipalName,
      }));

      if (searchId !== searchRef.current) {
        return;
      }

      setOptions(userOptions);
      setIsLoading(false);
    },
    [axiosInstance, getApiConfig, variable.id_property],
  );

  const handleSearchDebounced = useMemo(
    () => debounce(handleSearch, 300),
    [handleSearch],
  );

  return (
    <VariableFormItem variable={variable}>
      <Select
        placeholder="Start typing to search..."
        options={options}
        filterOption={false}
        showSearch={true}
        mode={variable.multiple ? 'multiple' : undefined}
        onSearch={handleSearchDebounced}
        loading={isLoading}
        style={{ width: '100%' }}
        disabled={disabled}
        optionRender={({ label, data }) => {
          return (
            <Card size="small">
              <Meta
                // avatar={
                //   <Avatar src="https://api.dicebear.com/7.x/miniavs/svg?seed=1" />
                // }
                title={label}
                description={data.userPrincipalName}
              />
            </Card>
          );
        }}
        notFoundContent={isLoading ? <Spin size="small" /> : undefined}
      />
    </VariableFormItem>
  );
};

export default UserSelect;
